import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  Clock, Calendar as CalendarIcon, Plus, Trash2, Edit2, Users, 
  Sun, Coffee, LogOut, AlertTriangle, Plane, MapPin, Save, RefreshCw,
  ChevronLeft, ChevronRight, Check, X, Bell, Stethoscope, User, Download
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import TeamVacationGrid from '../TeamVacationGrid';
import formatApiError from '../../utils/formatApiError';

const MADRID_TZ = 'Europe/Madrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRY_TYPES = [
  { value: 'entrada', label: 'Entrada', icon: Sun, color: 'bg-green-100 text-green-800' },
  { value: 'salida_comer', label: 'Salida a comer', icon: Coffee, color: 'bg-yellow-100 text-yellow-800' },
  { value: 'entrada_comer', label: 'Vuelta de comer', icon: Coffee, color: 'bg-blue-100 text-blue-800' },
  { value: 'salida_medico', label: 'Salida Médico', icon: Stethoscope, color: 'bg-pink-100 text-pink-800' },
  { value: 'entrada_medico', label: 'Entrada Médico', icon: Stethoscope, color: 'bg-pink-100 text-pink-800' },
  { value: 'salida_personal', label: 'Salida Asuntos', icon: User, color: 'bg-orange-100 text-orange-800' },
  { value: 'entrada_personal', label: 'Entrada Asuntos', icon: User, color: 'bg-orange-100 text-orange-800' },
  { value: 'salida', label: 'Fin de jornada', icon: LogOut, color: 'bg-red-100 text-red-800' },
  { value: 'incidencia', label: 'Incidencia', icon: AlertTriangle, color: 'bg-fuchsia-100 text-fuchsia-800' },
];

const INCIDENCIA_TYPES = [
  { value: 'medico', label: 'Médico' },
  { value: 'personal', label: 'Personal' },
  { value: 'otro', label: 'Otro' },
];

const COMUNIDADES = [
  "Andalucía", "Aragón", "Asturias", "Baleares", "Canarias", "Cantabria",
  "Castilla-La Mancha", "Castilla y León", "Cataluña", "Comunidad Valenciana",
  "Extremadura", "Galicia", "La Rioja", "Madrid", "Murcia", "Navarra", "País Vasco", "Ceuta", "Melilla"
];

const TimeTrackingTab = ({ isSuperadmin: propIsSuperadmin, currentUser: propCurrentUser }) => {
  const { token, user: authUser, isSuperadmin: authIsSuperadmin } = useAuth();
  const { language } = useLanguage();
  
  // Use props if provided, otherwise fallback to auth context
  const isSuperadmin = propIsSuperadmin !== undefined ? propIsSuperadmin : authIsSuperadmin;
  const currentUser = propCurrentUser || authUser;
  
  // State
  const [activeTab, setActiveTab] = useState('fichajes');
  const [loading, setLoading] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  // Historical date-range filters + entry-type filter for the "Fichajes" tab
  const [dateFrom, setDateFrom] = useState(startOfMonth(new Date()));
  const [dateTo, setDateTo] = useState(new Date());
  const [entryTypeFilter, setEntryTypeFilter] = useState('all');
  const [entriesTotal, setEntriesTotal] = useState(0);
  
  // Time entries state
  const [entries, setEntries] = useState([]);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    worker_id: '',
    entry_type: 'entrada',
    timestamp: '',
    notes: '',
    incidencia_type: ''
  });

  // Feb 2026 · Worked-hours quick summary (day / week / month)
  const [workedSummary, setWorkedSummary] = useState(null);
  const [workedSummaryLoading, setWorkedSummaryLoading] = useState(false);
  
  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    date: '',
    name: '',
    type: 'nacional',
    comunidad: '',
    worker_id: ''
  });
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  
  // Vacations state
  const [vacations, setVacations] = useState([]);
  const [vacationDays, setVacationDays] = useState([]);
  const [selectedVacationDates, setSelectedVacationDates] = useState([]);
  const [vacationMonth, setVacationMonth] = useState(new Date());
  
  // Schedules state (configuration)
  const [schedules, setSchedules] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  // Edit vacation days state
  const [editingVacationWorker, setEditingVacationWorker] = useState(null);
  const [vacationTotalDays, setVacationTotalDays] = useState(22);
  
  // Export state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportWorker, setExportWorker] = useState('all');
  
  // Festivos por trabajador
  const [holidayWorkerFilter, setHolidayWorkerFilter] = useState('all');
  
  // Helper: Only superadmin can edit/delete records
  const canEdit = isSuperadmin;
  
  // Fetch workers
  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/time-tracking/workers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkers(response.data.workers);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, [token]);
  
  // Fetch schedules
  const fetchSchedules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/time-tracking/schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedules(response.data.schedules);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, [token]);
  
  // Fetch time entries (historical, date-range + optional filters)
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
      params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      if (selectedWorker) params.append('worker_id', selectedWorker);
      if (entryTypeFilter && entryTypeFilter !== 'all') params.append('entry_type', entryTypeFilter);
      params.append('limit', '500');

      const response = await axios.get(`${API}/admin/time-tracking/entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries);
      setEntriesTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Error al cargar fichajes');
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, selectedWorker, entryTypeFilter]);
  
  // Fetch holidays
  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/time-tracking/holidays?year=${holidayYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data.holidays);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  }, [token, holidayYear]);
  
  // Fetch vacations
  const fetchVacations = useCallback(async () => {
    try {
      const year = vacationMonth.getFullYear();
      const [vacResponse, daysResponse] = await Promise.all([
        axios.get(`${API}/admin/time-tracking/vacations?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/time-tracking/vacation-days?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setVacations(vacResponse.data.vacations);
      setVacationDays(daysResponse.data.vacation_days);
    } catch (error) {
      console.error('Error fetching vacations:', error);
    }
  }, [token, vacationMonth]);
  
  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);
  
  useEffect(() => {
    if (activeTab === 'fichajes') fetchEntries();
    if (activeTab === 'festivos') fetchHolidays();
    if (activeTab === 'vacaciones') fetchVacations();
    if (activeTab === 'configuracion') fetchSchedules();
  }, [activeTab, fetchEntries, fetchHolidays, fetchVacations, fetchSchedules]);

  // Feb 2026 · Fetch worked-hours quick summary whenever the fichajes tab
  // is active or the selected worker changes.
  const fetchWorkedSummary = useCallback(async () => {
    if (activeTab !== 'fichajes') return;
    setWorkedSummaryLoading(true);
    try {
      const params = {};
      if (selectedWorker) params.worker_id = selectedWorker;
      const { data } = await axios.get(`${API}/admin/time-tracking/worked-hours-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setWorkedSummary(data);
    } catch (e) {
      setWorkedSummary(null);
    } finally {
      setWorkedSummaryLoading(false);
    }
  }, [activeTab, selectedWorker, token]);

  useEffect(() => {
    fetchWorkedSummary();
  }, [fetchWorkedSummary]);
  
  // Handle update schedule
  const handleUpdateSchedule = async (workerId, updates) => {
    try {
      await axios.post(`${API}/admin/time-tracking/schedules`, {
        worker_id: workerId,
        ...updates
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración actualizada');
      fetchSchedules();
      setEditingSchedule(null);
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };
  
  // Export fichajes to CSV
  const handleExportFichajes = async (workerId, year, month) => {
    setExportLoading(true);
    try {
      const params = { year };
      if (month) params.month = month;
      if (workerId && workerId !== 'all') params.worker_id = workerId;
      
      const response = await axios.get(`${API}/admin/time-tracking/export`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const workerName = workerId === 'all' ? 'todos' : workers.find(w => w.id === workerId)?.name || workerId;
      const filename = month 
        ? `fichajes_${workerName}_${year}-${String(month).padStart(2, '0')}.csv`
        : `fichajes_${workerName}_${year}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Fichajes exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar fichajes');
    } finally {
      setExportLoading(false);
    }
  };
  
  // Handlers
  const handleCreateEntry = async () => {
    try {
      const data = {
        ...entryForm,
        timestamp: entryForm.timestamp ? new Date(entryForm.timestamp).toISOString() : new Date().toISOString()
      };
      
      if (editingEntry) {
        await axios.put(`${API}/admin/time-tracking/entries/${editingEntry.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Fichaje actualizado');
      } else {
        await axios.post(`${API}/admin/time-tracking/entries`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Fichaje registrado');
      }
      
      setShowEntryDialog(false);
      setEditingEntry(null);
      setEntryForm({ worker_id: '', entry_type: 'entrada', timestamp: '', notes: '', incidencia_type: '' });
      fetchEntries();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al guardar fichaje'));
    }
  };
  
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('¿Eliminar este fichaje?')) return;
    try {
      await axios.delete(`${API}/admin/time-tracking/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fichaje eliminado');
      fetchEntries();
    } catch (error) {
      toast.error('Error al eliminar fichaje');
    }
  };
  
  const handleCreateHoliday = async () => {
    try {
      if (editingHoliday) {
        await axios.put(`${API}/admin/time-tracking/holidays/${editingHoliday.id}`, holidayForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Festivo actualizado');
      } else {
        await axios.post(`${API}/admin/time-tracking/holidays`, holidayForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Festivo creado');
      }
      
      setShowHolidayDialog(false);
      setEditingHoliday(null);
      setHolidayForm({ date: '', name: '', type: 'nacional', comunidad: '' });
      fetchHolidays();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al guardar festivo'));
    }
  };
  
  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm('¿Eliminar este festivo?')) return;
    try {
      await axios.delete(`${API}/admin/time-tracking/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Festivo eliminado');
      fetchHolidays();
    } catch (error) {
      toast.error('Error al eliminar festivo');
    }
  };
  
  // Handle approve/reject vacation request
  const handleApproveVacation = async (dayId, approve, rejectReason = '') => {
    try {
      await axios.put(`${API}/admin/time-tracking/vacation-days/${dayId}/approve`, {
        approve,
        reject_reason: rejectReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(approve ? 'Vacaciones aprobadas' : 'Vacaciones rechazadas');
      fetchVacations();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al procesar solicitud'));
    }
  };
  
  // Handle bulk approve/reject
  const handleBulkApprove = async (approve) => {
    const pendingDays = vacationDays.filter(d => d.status === 'pendiente');
    if (pendingDays.length === 0) {
      toast.error('No hay solicitudes pendientes');
      return;
    }
    
    const confirmMsg = approve 
      ? `¿Aprobar ${pendingDays.length} solicitud(es)?`
      : `¿Rechazar ${pendingDays.length} solicitud(es)?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.put(`${API}/admin/time-tracking/vacation-days/approve-bulk`, {
        day_ids: pendingDays.map(d => d.id),
        approve,
        reject_reason: approve ? '' : 'Solicitud rechazada por el administrador'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${pendingDays.length} solicitudes ${approve ? 'aprobadas' : 'rechazadas'}`);
      fetchVacations();
    } catch (error) {
      toast.error('Error al procesar solicitudes');
    }
  };
  
  const handleAddVacationDays = async () => {
    if (!selectedWorker || selectedVacationDates.length === 0) {
      toast.error('Selecciona un trabajador y al menos una fecha');
      return;
    }
    
    try {
      const dates = selectedVacationDates.map(d => format(d, 'yyyy-MM-dd'));
      await axios.post(`${API}/admin/time-tracking/vacation-days`, {
        worker_id: selectedWorker,
        dates,
        notes: ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${dates.length} días de vacaciones añadidos`);
      setSelectedVacationDates([]);
      fetchVacations();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al añadir vacaciones'));
    }
  };
  
  const handleDeleteVacationDay = async (dayId) => {
    if (!window.confirm('¿Eliminar este día de vacaciones?')) return;
    try {
      await axios.delete(`${API}/admin/time-tracking/vacation-days/${dayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Día eliminado');
      fetchVacations();
    } catch (error) {
      toast.error('Error al eliminar día');
    }
  };
  
  // Update vacation total days
  const handleUpdateVacationDays = async () => {
    if (!editingVacationWorker) return;
    try {
      await axios.post(`${API}/admin/time-tracking/vacations`, {
        worker_id: editingVacationWorker.id,
        year: vacationMonth.getFullYear(),
        total_days: vacationTotalDays
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vacaciones actualizadas: ${vacationTotalDays} días`);
      setEditingVacationWorker(null);
      fetchVacations();
    } catch (error) {
      toast.error('Error al actualizar vacaciones');
    }
  };
  
  const getEntryTypeInfo = (type) => ENTRY_TYPES.find(t => t.value === type) || ENTRY_TYPES[0];
  
  // Calendar helpers for vacations
  const monthStart = startOfMonth(vacationMonth);
  const monthEnd = endOfMonth(vacationMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const isVacationDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return vacationDays.some(d => d.date === dateStr && (!selectedWorker || d.worker_id === selectedWorker));
  };
  
  const isHolidayDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.some(h => h.date === dateStr);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6 text-primary" />
            Control de Fichajes
          </h2>
          <p className="text-muted-foreground">Gestión de jornada laboral, festivos y vacaciones</p>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="fichajes" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Fichajes
          </TabsTrigger>
          <TabsTrigger value="festivos" className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            Festivos
          </TabsTrigger>
          <TabsTrigger value="vacaciones" className="flex items-center gap-2">
            <Plane className="h-4 w-4" />
            Vacaciones
          </TabsTrigger>
          <TabsTrigger value="configuracion" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Configuración
          </TabsTrigger>
        </TabsList>
        
        {/* FICHAJES TAB */}
        <TabsContent value="fichajes" className="space-y-4">
          {/* Feb 2026 · Vista rápida de horas trabajadas · día / semana / mes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: 'today', label: 'Hoy', color: 'from-emerald-500 to-emerald-600', icon: '📅' },
              { key: 'this_week', label: 'Esta semana', color: 'from-sky-500 to-sky-600', icon: '📊', dateKeys: true },
              { key: 'this_month', label: 'Este mes', color: 'from-violet-500 to-violet-600', icon: '📈', dateKeys: true },
            ].map((card) => {
              const bucket = workedSummary?.[card.key];
              const h = bucket?.hours ?? 0;
              const m = bucket?.minutes ?? 0;
              const range = card.dateKeys && bucket
                ? `${bucket.start} → ${bucket.end}`
                : (workedSummary?.reference_date || '');
              return (
                <Card key={card.key} className={`overflow-hidden border-0 bg-gradient-to-br ${card.color} text-white shadow-md`} data-testid={`worked-summary-${card.key}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-1 opacity-90">
                      <span className="text-sm font-medium">{card.icon} {card.label}</span>
                      {workedSummaryLoading && <span className="text-xs opacity-75">⟳</span>}
                    </div>
                    <div className="text-3xl font-bold leading-tight">
                      {h}<span className="text-lg font-normal opacity-90"> h </span>
                      {String(m).padStart(2, '0')}<span className="text-lg font-normal opacity-90"> min</span>
                    </div>
                    <div className="text-xs opacity-80 mt-1">
                      {selectedWorker
                        ? (workers.find(w => w.id === selectedWorker)?.name || 'Trabajador seleccionado')
                        : 'Todos los trabajadores'}
                    </div>
                    <div className="text-[10px] opacity-70 mt-0.5">{range}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Export section — superadmin only */}
          {canEdit && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">Exportar Fichajes</CardTitle>
                    <CardDescription>Descarga los fichajes en formato CSV</CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={exportWorker} onValueChange={setExportWorker}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Trabajador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los trabajadores</SelectItem>
                        {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportFichajes(exportWorker, dateFrom.getFullYear(), dateFrom.getMonth() + 1)}
                      disabled={exportLoading}
                      data-testid="export-fichajes-month-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {format(dateFrom, 'MMMM yyyy', { locale: es })}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExportFichajes(exportWorker, dateFrom.getFullYear())}
                      disabled={exportLoading}
                      data-testid="export-fichajes-year-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Año {dateFrom.getFullYear()}
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Histórico de Fichajes</CardTitle>
                  <CardDescription>
                    {canEdit
                      ? 'Filtra por trabajador, rango de fechas y tipo. Edita o elimina como superadmin.'
                      : 'Solo lectura. Filtra por trabajador, rango de fechas y tipo.'}
                    {' — '}<strong>{entriesTotal}</strong> fichaje(s) encontrados
                  </CardDescription>
                </div>
                {canEdit && (
                  <Button
                    onClick={() => {
                      setEditingEntry(null);
                      setEntryForm({
                        worker_id: selectedWorker || '',
                        entry_type: 'entrada',
                        timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
                        notes: '',
                        incidencia_type: ''
                      });
                      setShowEntryDialog(true);
                    }}
                    data-testid="admin-new-entry-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nuevo Fichaje
                  </Button>
                )}
              </div>

              {/* Filters row */}
              <div className="flex flex-wrap items-end gap-2 pt-3">
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Trabajador</Label>
                  <Select value={selectedWorker || "all"} onValueChange={(v) => setSelectedWorker(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[220px]" data-testid="admin-filter-worker">
                      <SelectValue placeholder="Todos los trabajadores" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {workers.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Desde</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[170px] justify-start" data-testid="admin-filter-date-from">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(dateFrom, 'dd/MM/yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={dateFrom} onSelect={(d) => d && setDateFrom(d)} locale={es} />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Hasta</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[170px] justify-start" data-testid="admin-filter-date-to">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(dateTo, 'dd/MM/yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={dateTo} onSelect={(d) => d && setDateTo(d)} locale={es} />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={entryTypeFilter} onValueChange={setEntryTypeFilter}>
                    <SelectTrigger className="w-[190px]" data-testid="admin-filter-entry-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los tipos</SelectItem>
                      {ENTRY_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedWorker('');
                    setDateFrom(startOfMonth(new Date()));
                    setDateTo(new Date());
                    setEntryTypeFilter('all');
                  }}
                  data-testid="admin-filter-reset"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Limpiar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Cargando...</div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No hay fichajes para los filtros seleccionados
                </div>
              ) : (
                <div className="max-h-[600px] overflow-auto border rounded">
                  <table className="w-full caption-bottom text-sm" data-testid="admin-entries-table">
                    <thead>
                      <tr className="border-b bg-white">
                        <th className="sticky top-0 z-10 bg-white text-left h-10 px-3 font-medium text-muted-foreground">Fecha</th>
                        <th className="sticky top-0 z-10 bg-white text-left h-10 px-3 font-medium text-muted-foreground">Trabajador</th>
                        <th className="sticky top-0 z-10 bg-white text-left h-10 px-3 font-medium text-muted-foreground">Tipo</th>
                        <th className="sticky top-0 z-10 bg-white text-left h-10 px-3 font-medium text-muted-foreground">Hora</th>
                        <th className="sticky top-0 z-10 bg-white text-left h-10 px-3 font-medium text-muted-foreground">Notas</th>
                        {canEdit && (
                          <th className="sticky top-0 z-10 bg-white text-right h-10 px-3 font-medium text-muted-foreground">Acciones</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map(entry => {
                        const typeInfo = getEntryTypeInfo(entry.entry_type);
                        const Icon = typeInfo.icon;
                        const wasEdited = Array.isArray(entry.edit_history) && entry.edit_history.length > 0;
                        return (
                          <tr key={entry.id} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {format(parseISO(entry.date), 'dd/MM/yyyy')}
                              {wasEdited && (
                                <span className="ml-2 text-[10px] rounded bg-amber-100 text-amber-800 px-1 py-0.5" title={`Editado ${entry.edit_history.length} vez(es)`}>editado</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <div>
                                <p className="font-medium">{entry.worker_name}</p>
                                <p className="text-xs text-muted-foreground">{entry.worker_email}</p>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <Badge className={typeInfo.color}>
                                <Icon className="h-3 w-3 mr-1" />
                                {typeInfo.label}
                              </Badge>
                              {entry.incidencia_type && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({entry.incidencia_type})
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono">
                              {formatInTimeZone(parseISO(entry.timestamp), MADRID_TZ, 'HH:mm')}
                            </td>
                            <td className="px-3 py-2 max-w-[240px] truncate" title={entry.notes || ''}>
                              {entry.notes || '-'}
                            </td>
                            {canEdit && (
                              <td className="px-3 py-2 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingEntry(entry);
                                    setEntryForm({
                                      worker_id: entry.worker_id,
                                      entry_type: entry.entry_type,
                                      timestamp: formatInTimeZone(parseISO(entry.timestamp), MADRID_TZ, "yyyy-MM-dd'T'HH:mm"),
                                      notes: entry.notes || '',
                                      incidencia_type: entry.incidencia_type || ''
                                    });
                                    setShowEntryDialog(true);
                                  }}
                                  data-testid={`admin-edit-entry-${entry.id}`}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                  data-testid={`admin-delete-entry-${entry.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* FESTIVOS TAB */}
        <TabsContent value="festivos" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <CardTitle className="text-lg">Gestión de Festivos</CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={String(holidayYear)} onValueChange={(v) => setHolidayYear(Number(v))}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {canEdit && (
                    <Button onClick={() => {
                      setEditingHoliday(null);
                      setHolidayForm({ date: '', name: '', type: 'nacional', comunidad: '', worker_id: '' });
                      setShowHolidayDialog(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Festivo
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Nacionales */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-800">Nacionales</Badge>
                    <span className="text-xs text-muted-foreground">({holidays.filter(h => h.type === 'nacional').length})</span>
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {holidays.filter(h => h.type === 'nacional').map(h => (
                      <div key={h.id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{h.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(h.date), 'PPP', { locale: es })}
                          </p>
                        </div>
                        {isSuperadmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingHoliday(h);
                              setHolidayForm({ date: h.date, name: h.name, type: h.type, comunidad: h.comunidad || '', worker_id: '' });
                              setShowHolidayDialog(true);
                            }}>
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(h.id)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {holidays.filter(h => h.type === 'nacional').length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No hay festivos nacionales</p>
                    )}
                  </div>
                </div>
                
                {/* Locales */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-800">Locales</Badge>
                    <span className="text-xs text-muted-foreground">({holidays.filter(h => h.type === 'local').length})</span>
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {holidays.filter(h => h.type === 'local').map(h => (
                      <div key={h.id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{h.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(h.date), 'PPP', { locale: es })}
                          </p>
                          <Badge variant="outline" className="text-xs mt-1">
                            <MapPin className="h-2 w-2 mr-1" />
                            {h.comunidad}
                          </Badge>
                        </div>
                        {isSuperadmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingHoliday(h);
                              setHolidayForm({ date: h.date, name: h.name, type: h.type, comunidad: h.comunidad || '', worker_id: '' });
                              setShowHolidayDialog(true);
                            }}>
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(h.id)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {holidays.filter(h => h.type === 'local').length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No hay festivos locales</p>
                    )}
                  </div>
                </div>
                
                {/* Personales por Trabajador */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-800">Por Trabajador</Badge>
                    <span className="text-xs text-muted-foreground">({holidays.filter(h => h.type === 'personal').length})</span>
                  </h3>
                  <div className="mb-3">
                    <Select value={holidayWorkerFilter} onValueChange={setHolidayWorkerFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Filtrar por trabajador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los trabajadores</SelectItem>
                        {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {holidays
                      .filter(h => h.type === 'personal')
                      .filter(h => holidayWorkerFilter === 'all' || h.worker_id === holidayWorkerFilter)
                      .map(h => (
                      <div key={h.id} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{h.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseISO(h.date), 'PPP', { locale: es })}
                          </p>
                          <Badge variant="outline" className="text-xs mt-1">
                            <User className="h-2 w-2 mr-1" />
                            {h.worker_name || 'Trabajador'}
                          </Badge>
                        </div>
                        {isSuperadmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingHoliday(h);
                              setHolidayForm({ date: h.date, name: h.name, type: h.type, comunidad: '', worker_id: h.worker_id || '' });
                              setShowHolidayDialog(true);
                            }}>
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteHoliday(h.id)}>
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {holidays.filter(h => h.type === 'personal').filter(h => holidayWorkerFilter === 'all' || h.worker_id === holidayWorkerFilter).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No hay festivos personales</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* VACACIONES TAB */}
        <TabsContent value="vacaciones" className="space-y-4">
          {/* Annual multicolor team calendar — replaces the previous monthly-only view. */}
          <TeamVacationGrid />
        </TabsContent>
        
        {/* CONFIGURACIÓN TAB */}
        <TabsContent value="configuracion" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Configuración de Trabajadores</CardTitle>
              <CardDescription>
                Configura la comunidad autónoma y horario de cada trabajador
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trabajador</TableHead>
                    <TableHead>Comunidad Autónoma</TableHead>
                    <TableHead>Horas/Día</TableHead>
                    <TableHead>Pausa Comida</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(worker => {
                    const schedule = schedules.find(s => s.worker_id === worker.id) || {};
                    const isEditing = editingSchedule === worker.id;
                    
                    return (
                      <TableRow key={worker.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{worker.name || 'Sin nombre'}</p>
                            <p className="text-sm text-muted-foreground">{worker.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Select 
                              defaultValue={schedule.comunidad || "none"}
                              onValueChange={(v) => {
                                const newSchedule = {...schedule, comunidad: v === "none" ? "" : v};
                                setSchedules(prev => prev.map(s => s.worker_id === worker.id ? {...s, ...newSchedule} : s));
                              }}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Sin comunidad</SelectItem>
                                {COMUNIDADES.map(c => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline">
                              {schedule.comunidad || 'Sin asignar'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input 
                              type="number" 
                              step="0.5"
                              min="1"
                              max="12"
                              className="w-20"
                              defaultValue={schedule.work_hours_per_day || 8}
                              onChange={(e) => {
                                const newSchedule = {...schedule, work_hours_per_day: parseFloat(e.target.value)};
                                setSchedules(prev => prev.map(s => s.worker_id === worker.id ? {...s, ...newSchedule} : s));
                              }}
                            />
                          ) : (
                            <span>{schedule.work_hours_per_day || 8}h</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <Input 
                              type="number" 
                              step="15"
                              min="0"
                              max="120"
                              className="w-20"
                              defaultValue={schedule.lunch_break_minutes || 60}
                              onChange={(e) => {
                                const newSchedule = {...schedule, lunch_break_minutes: parseInt(e.target.value)};
                                setSchedules(prev => prev.map(s => s.worker_id === worker.id ? {...s, ...newSchedule} : s));
                              }}
                            />
                          ) : (
                            <span>{schedule.lunch_break_minutes || 60} min</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => {
                                  const currentSchedule = schedules.find(s => s.worker_id === worker.id) || {};
                                  handleUpdateSchedule(worker.id, {
                                    comunidad: currentSchedule.comunidad || '',
                                    work_hours_per_day: currentSchedule.work_hours_per_day || 8,
                                    lunch_break_minutes: currentSchedule.lunch_break_minutes || 60
                                  });
                                }}
                              >
                                <Save className="h-4 w-4 mr-1" />
                                Guardar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setEditingSchedule(null);
                                  fetchSchedules();
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setEditingSchedule(worker.id)}
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Editar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No hay trabajadores configurados
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Entry Dialog */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Editar Fichaje' : 'Nuevo Fichaje'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Trabajador</Label>
              <Select 
                value={entryForm.worker_id} 
                onValueChange={(v) => setEntryForm({...entryForm, worker_id: v})}
                disabled={!!editingEntry}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar trabajador" />
                </SelectTrigger>
                <SelectContent>
                  {workers.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Fichaje</Label>
              <Select 
                value={entryForm.entry_type} 
                onValueChange={(v) => setEntryForm({...entryForm, entry_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTRY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <t.icon className="h-4 w-4" />
                        {t.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {entryForm.entry_type === 'incidencia' && (
              <div>
                <Label>Tipo de Incidencia</Label>
                <Select 
                  value={entryForm.incidencia_type} 
                  onValueChange={(v) => setEntryForm({...entryForm, incidencia_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {INCIDENCIA_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Fecha y Hora</Label>
              <Input 
                type="datetime-local"
                value={entryForm.timestamp}
                onChange={(e) => setEntryForm({...entryForm, timestamp: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Notas</Label>
              <Input 
                value={entryForm.notes}
                onChange={(e) => setEntryForm({...entryForm, notes: e.target.value})}
                placeholder="Notas opcionales..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateEntry}>
              {editingEntry ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Holiday Dialog */}
      <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Editar Festivo' : 'Nuevo Festivo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Fecha</Label>
              <Input 
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({...holidayForm, date: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Nombre del Festivo</Label>
              <Input 
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({...holidayForm, name: e.target.value})}
                placeholder="Ej: Día de la Hispanidad"
              />
            </div>
            
            <div>
              <Label>Tipo</Label>
              <Select 
                value={holidayForm.type} 
                onValueChange={(v) => setHolidayForm({...holidayForm, type: v, comunidad: '', worker_id: ''})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nacional">Nacional (todos los trabajadores)</SelectItem>
                  <SelectItem value="local">Local (por Comunidad Autónoma)</SelectItem>
                  <SelectItem value="personal">Personal (trabajador específico)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {holidayForm.type === 'local' && (
              <div>
                <Label>Comunidad Autónoma</Label>
                <Select 
                  value={holidayForm.comunidad} 
                  onValueChange={(v) => setHolidayForm({...holidayForm, comunidad: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar comunidad" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMUNIDADES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {holidayForm.type === 'personal' && (
              <div>
                <Label>Trabajador</Label>
                <Select 
                  value={holidayForm.worker_id} 
                  onValueChange={(v) => setHolidayForm({...holidayForm, worker_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar trabajador" />
                  </SelectTrigger>
                  <SelectContent>
                    {workers.filter(w => w.role === 'Trabajador' || w.role === 'worker').map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name || w.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHolidayDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateHoliday}>
              {editingHoliday ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTrackingTab;
